import { useEffect, useState } from 'react'
import clsx from 'clsx'
import { AlertOctagon, CheckCircle2, Phone, Plus, Send } from 'lucide-react'
import { EYEBROW } from '../cc/tokens'
import { actOnBusBreakdown, fetchBusBreakdownCodes, openBusBreakdown } from './useBusApi'
import type { BusBreakdown, BusBreakdownCode } from './types'

/** Nagpur's breakdown module. Reuses site_breakdown (site='nagpur') -- the same
 *  multi-tenant table MBMT/STAR/Ports use, not Ultratech's vehicle_breakdown. No
 *  charging-TAT-exclusion, no WhatsApp: matches MBMT's own breakdown module's actual
 *  behaviour, per the 2026-09-25 design note ("similar to MBMT" means MBMT as it is, not
 *  a superset of it). No live-position autofill yet (see serving/app.py's comment on
 *  _run_site_breakdown_position) -- the operator types the plate directly.
 *
 *  `breakdowns`/`loaded` are props now, not this component's own 15s poll --
 *  NagpurBusOpsShell already polls the identical fetchBusBreakdowns(true) on the same
 *  cadence for the Fleet drawer's exception chip, so a second poll here only ever
 *  duplicated a request (CLAUDE.md's live-data section). `onMutated` lets this panel ask
 *  the shell to refresh immediately after a write (open/call/dispatch/resolve), the same
 *  "write, then re-pull the authoritative list" shape the old local refresh() had. */

const QUICK_ACTIONS: { id: string; label: string; icon: typeof Phone }[] = [
  { id: 'called', label: 'Called', icon: Phone },
  { id: 'dispatched', label: 'Dispatched', icon: Send },
  { id: 'resolve', label: 'Resolve', icon: CheckCircle2 },
]

function ago(ms: number | null): string {
  if (ms == null) return '—'
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000))
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.floor(s / 60)}m`
  return `${Math.floor(s / 3600)}h`
}

export function BusBreakdownsPanel(
  { breakdowns, loaded, onMutated }: {
    breakdowns: BusBreakdown[]
    loaded: boolean
    onMutated: () => Promise<unknown>
  },
): JSX.Element {
  const [codes, setCodes] = useState<BusBreakdownCode[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<number | 'new' | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [plate, setPlate] = useState('')
  const [category, setCategory] = useState('OTHER')
  const [note, setNote] = useState('')

  useEffect(() => {
    void fetchBusBreakdownCodes().then(setCodes).catch(() => {})
  }, [])

  async function submitOpen(): Promise<void> {
    const vid = plate.trim().toUpperCase()
    if (!vid) return
    setBusy('new')
    try {
      await openBusBreakdown({ vehicle_id: vid, category, note: note.trim() || undefined })
      setPlate(''); setNote(''); setFormOpen(false)
      await onMutated()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not open breakdown')
    } finally {
      setBusy(null)
    }
  }

  async function act(id: number, action: string): Promise<void> {
    setBusy(id)
    try {
      await actOnBusBreakdown(id, { action })
      await onMutated()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'action failed')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between border-b border-[rgba(15,23,42,0.06)] px-3 py-2">
        <span className="text-[12px] font-semibold text-[#0F172A]">Open breakdowns</span>
        <button
          type="button"
          onClick={() => setFormOpen((v) => !v)}
          className="flex items-center gap-1 rounded-full bg-[#0F172A] px-2.5 py-1 text-[11px] font-semibold text-white"
        >
          <Plus size={11} /> Report
        </button>
      </div>

      {formOpen && (
        <div className="space-y-2 border-b border-[rgba(15,23,42,0.06)] px-3 py-2.5">
          <label className="block">
            <span className={EYEBROW}>Plate</span>
            <input
              value={plate}
              onChange={(e) => setPlate(e.target.value)}
              placeholder="MH40DC…"
              className="mt-1 w-full rounded-[8px] border border-[rgba(15,23,42,0.10)] bg-white px-2 py-1.5 font-mono text-[12px] text-[#0F172A] outline-none"
            />
          </label>
          <label className="block">
            <span className={EYEBROW}>Category</span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-1 w-full rounded-[8px] border border-[rgba(15,23,42,0.10)] bg-white px-2 py-1.5 text-[12px] text-[#0F172A]"
            >
              {(codes.length ? codes : [{ code: 'OTHER', name: 'Other' }]).map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note…"
            className="min-h-[52px] w-full resize-none rounded-[8px] border border-[rgba(15,23,42,0.10)] bg-[#F8FAFC] px-2 py-1.5 text-[12px] text-[#0F172A] outline-none"
          />
          <button
            type="button"
            disabled={!plate.trim() || busy === 'new'}
            onClick={() => void submitOpen()}
            className="w-full rounded-[8px] bg-[#0F172A] px-3 py-1.5 text-[12px] font-semibold text-white disabled:opacity-40"
          >
            {busy === 'new' ? 'Opening…' : 'Open incident'}
          </button>
        </div>
      )}

      {error && (
        <p className="px-3 py-2 text-[11px] text-[#9F1239]">{error}</p>
      )}

      <div className="min-h-0 flex-1 overflow-auto px-2 pb-2">
        {loaded && breakdowns.length === 0 && (
          <p className="px-2 py-3 text-[12px] text-[#94A3B8]">No open breakdowns.</p>
        )}
        {breakdowns.map((b) => (
          <div
            key={b.breakdown_id}
            className="mb-1.5 rounded-[10px] border border-[rgba(244,63,94,0.20)] bg-[rgba(244,63,94,0.05)] px-3 py-2"
          >
            <div className="flex items-center gap-2">
              <AlertOctagon size={12} className="text-[#9F1239]" />
              <span className="font-mono text-[12.5px] font-semibold text-[#0F172A]">{b.vehicle_id}</span>
              <span className="ml-auto font-mono text-[10.5px] tabular-nums text-[#94A3B8]">
                {ago(b.opened_ts)}
              </span>
            </div>
            <p className="mt-0.5 pl-[18px] text-[11px] text-[#64748B]">
              {b.category}{b.note ? ` · ${b.note}` : ''}
            </p>
            <div className="mt-1.5 flex gap-1 pl-[18px]">
              {QUICK_ACTIONS.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  disabled={busy === b.breakdown_id}
                  onClick={() => void act(b.breakdown_id, a.id)}
                  className={clsx(
                    'flex items-center gap-1 rounded-full border border-[rgba(15,23,42,0.10)] bg-white px-2 py-0.5',
                    'text-[10.5px] font-medium text-[#334155] disabled:opacity-40',
                  )}
                >
                  <a.icon size={10} /> {a.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
